// client/src/pages/seo/CreateDigitalDailyMenuEn.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "A daily menu should be easy to update quickly",
  },
  {
    id: "why",
    label: "Why does a digital daily menu make sense?",
  },
  {
    id: "use-cases",
    label: "What can a digital daily menu be used for?",
  },
  {
    id: "workflow",
    label: "How do you create a digital daily menu in practice?",
  },
  {
    id: "content",
    label: "What should a good daily menu include?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen with digital daily menus?",
  },
  {
    id: "recommendation",
    label: "How would I start as a restaurant owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you create a digital daily menu",
  },
  {
    id: "faq",
    label: "FAQ about digital daily menus",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function CreateDigitalDailyMenuEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Create a Digital Daily Menu: Lunch Specials and Seasonal Offers | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/create-digital-daily-menu",
          de: "/de/tageskarte-digital-erstellen",
          no: "/no/opprett-digital-dagsmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Create a digital daily menu: update lunch specials, weekly offers
              and seasonal dishes online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A daily menu only works when it is current. That is why paper
                menus and PDFs often become inconvenient: today there is a new
                lunch special, tomorrow one item is sold out, next week a
                seasonal offer begins.
              </p>

              <p className="seoHero__text">
                A digital daily menu makes these changes easier. The restaurant
                can update dishes, prices, descriptions and photos online, while
                the QR code stays the same.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>A daily menu should be easy to update quickly</h2>

          <p>
            A standard menu can remain the same for months. A daily menu cannot.
            It reacts to purchasing, seasonality, kitchen capacity, guest demand
            and spontaneous ideas from the team.
          </p>

          <p>
            That is why a digital daily menu should not depend on a designer,
            new PDF export or new print material every time something changes.
            The restaurant should be able to update current offers quickly and
            keep the QR code stable.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Slow workflow</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Daily dish is decided internally.
                </li>
                <li className="text__seo-li">
                  PDF or template needs editing.
                </li>
                <li className="text__seo-li">New file is exported.</li>
                <li className="text__seo-li">
                  QR link or website is checked.
                </li>
                <li className="text__seo-li">
                  Guests may still see old offers.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better workflow</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Add the daily dish in the admin area.
                </li>
                <li className="text__seo-li">
                  Add price and short description.
                </li>
                <li className="text__seo-li">Optionally add a photo.</li>
                <li className="text__seo-li">Save changes.</li>
                <li className="text__seo-li">
                  Daily menu is updated online.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test a small daily menu first</h3>

          <p>
            Add three to five daily offers, open the menu on a smartphone and
            check whether the workflow is simple enough for your team. A small
            test is usually better than building a large daily menu before you
            know how it will work in daily service.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why does a digital daily menu make sense?</h2>

            <p>
              Daily menus, lunch specials and weekly offers change more often
              than the main menu. That is why they need a different way of being
              managed.
            </p>

            <p>
              If guests see a lunch special online that is no longer available,
              they are disappointed. If a new offer is not visible, the
              restaurant loses attention. A digital daily menu helps avoid these
              gaps.
            </p>

            <h3>Daily offers need faster updates than the main menu</h3>

            <p>
              A restaurant may keep its main dishes for a long time, but daily
              offers often depend on ingredients, seasonality, kitchen planning
              and guest demand. The easier it is to update them, the more useful
              they become.
            </p>

            <p>
              This is especially relevant for restaurants that change offers
              several times per week or use lunch specials to attract guests
              during specific hours.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>What can a digital daily menu be used for?</h2>

          <p>
            A digital daily menu is not only for classic lunch specials. It can
            be used for any content that changes often or is available only for
            a limited time.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Lunch specials.</li>
            <li className="text__seo-li">Daily dishes.</li>
            <li className="text__seo-li">Weekly menu.</li>
            <li className="text__seo-li">Seasonal dishes.</li>
            <li className="text__seo-li">Chef recommendations.</li>
            <li className="text__seo-li">Business lunch.</li>
            <li className="text__seo-li">Limited-time offers.</li>
            <li className="text__seo-li">Sold-out products.</li>
          </ul>

          <p>
            A digital daily menu is especially useful when offers need to be
            updated in the morning, before lunch service or during the day.
          </p>

          <VisualIdea>
            Smartphone with a daily menu: today’s recommendation, lunch special,
            seasonal dish and one sold-out item hidden from view.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How do you create a digital daily menu in practice?</h2>

            <p>
              The workflow should be simple enough that it does not depend on a
              designer or external agency. One responsible person in the
              restaurant should be able to update the daily menu.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Create a category:</strong> For example “Daily menu”,
                “Lunch specials” or “Today’s recommendations”.
              </li>

              <li className="text__seo-li">
                <strong>Add dishes:</strong> Add name, price and a short
                description.
              </li>

              <li className="text__seo-li">
                <strong>Add a photo optionally:</strong> For new or seasonal
                dishes, an image can help.
              </li>

              <li className="text__seo-li">
                <strong>Manage availability:</strong> Hide sold-out dishes or
                replace them.
              </li>

              <li className="text__seo-li">
                <strong>Save and check:</strong> Open the public menu on a
                smartphone.
              </li>
            </ol>

            <p>
              The QR code does not need to be reprinted every day. It still
              opens the digital menu, while the content behind it is updated.
            </p>
          </div>
        </section>

        <section id="content" className="acquaintance">
          <h2>What should a good daily menu include?</h2>

          <p>
            A daily menu should be short, clear and easy to understand. Guests
            do not want to search for a long time. They want to see what is
            special today.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Clear title.</li>
            <li className="text__seo-li">Short time period.</li>
            <li className="text__seo-li">Dish name.</li>
            <li className="text__seo-li">Price.</li>
            <li className="text__seo-li">Short description.</li>
            <li className="text__seo-li">Photo when useful.</li>
            <li className="text__seo-li">Availability note.</li>
            <li className="text__seo-li">Drink or set option.</li>
          </ul>

          <p>
            Daily menus often work best when they are not overloaded. Three to
            seven strong offers can be better than a long unfocused list.
          </p>

          <h3>A daily menu should feel like a recommendation</h3>

          <p>
            The goal is not to create a second full menu. The goal is to show
            guests what is worth choosing today: a lunch special, a seasonal
            dish, a chef recommendation or a limited offer.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen with digital daily menus?</h2>

            <p>
              A digital daily menu is useful only when it remains current and
              clear. Otherwise it becomes just another confusing menu page.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Daily dishes remain visible for several days.
                  </li>
                  <li className="text__seo-li">
                    Sold-out products are not hidden.
                  </li>
                  <li className="text__seo-li">
                    Prices do not match the POS or printed menu.
                  </li>
                  <li className="text__seo-li">
                    The daily menu becomes too long.
                  </li>
                  <li className="text__seo-li">
                    There is no clear title.
                  </li>
                  <li className="text__seo-li">
                    Photos look inconsistent or unsuitable.
                  </li>
                  <li className="text__seo-li">
                    No one is responsible for daily updates.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Label time-limited offers clearly.
                  </li>
                  <li className="text__seo-li">
                    Hide sold-out dishes immediately.
                  </li>
                  <li className="text__seo-li">
                    Check prices centrally.
                  </li>
                  <li className="text__seo-li">
                    Show only selected daily offers.
                  </li>
                  <li className="text__seo-li">
                    Use clear titles like “Today’s recommendations”.
                  </li>
                  <li className="text__seo-li">
                    Choose photos intentionally.
                  </li>
                  <li className="text__seo-li">
                    Assign one responsible person.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I start as a restaurant owner?</h2>

          <p>
            I would not make the daily menu huge from the beginning. I would
            start with one small section that actually changes regularly.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Create a “Today” category:</strong> This category contains
              only current offers.
            </li>

            <li className="text__seo-li">
              <strong>Add three to five dishes:</strong> With price and short
              description.
            </li>

            <li className="text__seo-li">
              <strong>Test one photo:</strong> Not for every dish, but for the
              strongest offer.
            </li>

            <li className="text__seo-li">
              <strong>Open the QR code:</strong> Check how the daily menu looks
              on a smartphone.
            </li>

            <li className="text__seo-li">
              <strong>Observe for one week:</strong> Is the menu used? Does the
              team refer to it?
            </li>

            <li className="text__seo-li">
              <strong>Expand later:</strong> Add weekly menu, seasonal offers or
              lunch specials after the first test.
            </li>
          </ol>

          <p>
            This approach keeps the first version simple and makes it easier to
            understand whether a digital daily menu really helps in daily
            operations.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to create a digital daily menu, start with a small
              test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Create a digital menu.</li>
              <li className="text__seo-li">Add a “Daily menu” category.</li>
              <li className="text__seo-li">Add three to five real offers.</li>
              <li className="text__seo-li">
                Add prices and short descriptions.
              </li>
              <li className="text__seo-li">
                Open the menu on a smartphone.
              </li>
              <li className="text__seo-li">
                Test the QR code in the restaurant.
              </li>
              <li className="text__seo-li">
                Update the offers again after one day.
              </li>
            </ul>

            <VisualIdea>
              A simple daily menu test: one category, three current offers, one
              QR code and a smartphone view that shows the updated dishes.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about digital daily menus</h2>

          <div className="seoFaq">
            <details>
              <summary>What is a digital daily menu?</summary>
              <p>
                It is an online-managed section of the menu that shows current
                daily dishes, lunch specials, weekly offers or seasonal items.
              </p>
            </details>

            <details>
              <summary>Do I need to reprint the QR code every day?</summary>
              <p>
                No. The QR code stays the same if it points to a stable menu
                link. Only the daily menu content is updated.
              </p>
            </details>

            <details>
              <summary>Can I hide sold-out daily specials?</summary>
              <p>
                Yes. This is one of the most useful advantages. A dish can be
                hidden and replaced with another offer.
              </p>
            </details>

            <details>
              <summary>How many dishes should a daily menu have?</summary>
              <p>
                For a first test, three to seven offers are often enough. The
                daily menu should stay clear and easy to understand.
              </p>
            </details>

            <details>
              <summary>Is a digital daily menu better than a PDF?</summary>
              <p>
                If offers change often, a digital daily menu is usually more
                practical because content can be updated directly.
              </p>
            </details>

            <details>
              <summary>Can I start for free?</summary>
              <p>
                Yes. For a first test, create a few daily offers and check the
                view through a QR code on a smartphone.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="menu"
          exclude="/en/create-digital-daily-menu"
        />
      </article>
    </main>
  );
}